// Refactor 6/18 context:
// This has no reason to be in it's own folder, could live in /shared/tos.
// Also it has no reason to be called 'container' - it now includes everything (and more)
// that's needed to show the modal. I'm leaving it in place to preserve git history,
// so one could easily go to the PR which originally introduced it -
// https://github.com/onemedical/patient-ui/pull/6398/ - for extra context.
// Refactor on 6/18 got rid of the extra complexity of nested components and updated
// the unit test to include the actual functionality test (that new doc gets signed).

import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { ApolloQueryResult } from '@apollo/client/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Observable, combineLatest } from 'rxjs';
import { map, tap, takeUntil } from 'rxjs/operators';

import { FeatureFlags } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { LegalDocumentsService, TermsOfService } from '@app/core/legal-documents';
import { MarkdownService } from '@app/core/markdown.service';
import { ContentBlock } from '@app/home/__generated__/content-block-graphql.service.types';
import { ContentBlockGraphQL } from '@app/home/content-block-graphql.service';

@Component({
  selector: 'om-tos-update-container',
  templateUrl: './tos-update-container.component.html',
  styleUrls: [],
})
export class ToSUpdateContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tosUpdateModal') tosUpdateModal: NgbModal;

  private unsignedTosTypes = new Map<string, TermsOfService>();
  private destroy$ = new Subject<void>();
  private showModal$: Observable<boolean>;
  private notificationEnabledFlag$: Observable<boolean>;
  private modalReference: NgbModalRef;
  protected modalContent$: Observable<SafeHtml>;

  private readonly fallbackModalContent =
    'We updated our Membership Terms of Services and Medical Terms of Service as of 2/28/2025. ' +
    'The Membership Terms of Service is now named the One Medical Terms of Use. Please review the ' +
    'updated terms carefully – the [One Medical Terms of Use](https://www.onemedical.com/membership-terms/) ' +
    'applies to your use of our Services as defined therein, and the [Medical Terms of Service]' +
    '(https://www.onemedical.com/medical-terms-service) applies to your use of our Medical Services as defined therein.' +
    '\n\n\nBy continuing, you agree to the One Medical Terms of Use and the Medical Terms of Service';

  constructor(
    private contentBlockGraphQL: ContentBlockGraphQL,
    private launchDarklyService: LaunchDarklyService,
    private legalDocumentsService: LegalDocumentsService,
    private markdownService: MarkdownService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.showModal$ = this.legalDocumentsService.getForSelf().valueChanges.pipe(
      tap((termsOfServices: TermsOfService[]) => this.updateUnsignedTosType(termsOfServices)),
      map((termsOfServices: TermsOfService[]) => this.hasUnsignedTermsOfService(termsOfServices)),
    );

    this.notificationEnabledFlag$ = this.launchDarklyService.featureFlag$(
      FeatureFlags.HOMESCREEN_NOTIFY_TERMS_OF_SERVICE,
      false,
    );

    this.modalContent$ = this.fetchContentBlock();
  }

  ngAfterViewInit() {
    combineLatest([this.showModal$, this.modalContent$, this.notificationEnabledFlag$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([showModal, modalContent, notificationEnabled]) => {
        if (showModal && modalContent && notificationEnabled) {
          this.modalReference = this.modalService.open(this.tosUpdateModal, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            modalDialogClass: 'modal-redesign no-sm-rounded-bottom',
            ariaLabelledBy: 'tos-update-modal',
          });
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleAcknowledged() {
    this.unsignedTosTypes.forEach((value: TermsOfService) => {
      this.legalDocumentsService.signForSelf(value).subscribe(() => {
        this.modalReference.close();
      });
    });
  }

  private fetchContentBlock(): Observable<SafeHtml> {
    return this.contentBlockGraphQL
      .fetch({
        section: 'terms-of-service',
        page: null,
        serviceAreaId: null,
        title: 'update-notification',
      })
      .pipe(
        map((result: ApolloQueryResult<ContentBlock>) =>
          this.markdownService.convert(result.data.contentBlock?.copy || this.fallbackModalContent),
        ),
      );
  }

  private updateUnsignedTosType(termsOfServices: TermsOfService[]) {
    termsOfServices.filter(tos => tos.signed === false).forEach(tos => this.unsignedTosTypes.set(tos.type, tos));
  }

  private hasUnsignedTermsOfService(termsOfServices: TermsOfService[]) {
    return termsOfServices.filter(tos => tos.signed === false).length > 0;
  }
}
